import React from 'react';
import { Badge, Button, Spinner, Tooltip } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import {
    PlusIcon,
    TrashIcon,
    ArrowsClockwiseIcon,
    ArrowSquareOutIcon
} from '@phosphor-icons/react';
import { IIntegrationsSet, PlatformConfig } from '../interface';
import {
    getSourceHeader,
    ConnectedSourceDetails
} from './SourceConfigurationUtils';
import { useAuth } from '../../../../hooks';

const META_ALLOWED_SCHEMAS: string[] = (import.meta.env.VITE_META_ALLOWED_USERS_SCHEMA || '')
    .split(',')
    .map((s: string) => s.trim())
    .filter(Boolean);

interface SourceConfigurationBlockProps {
    title: string;
    titleColorClass: string;
    icon: React.ReactNode;
    iconContainerClass: string;
    badgeColor: string;
    platforms: PlatformConfig[];
    connectedSources: IIntegrationsSet[];
    onConnect: (platform: PlatformConfig) => void;
    onDisconnect: (source: IIntegrationsSet, platform: PlatformConfig) => void;
    onSelectAccount: (source: IIntegrationsSet, platformId: string) => void;
    isAuthenticating: boolean;
    authenticatingPlatform: string | null;
    isEditMode: boolean;
}

const SourceConfigurationBlock: React.FC<SourceConfigurationBlockProps> = ({
    title,
    titleColorClass,
    icon,
    iconContainerClass,
    badgeColor,
    platforms,
    connectedSources,
    onConnect,
    onDisconnect,
    onSelectAccount,
    isAuthenticating,
    authenticatingPlatform,
    isEditMode,
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isMetaAllowed = !!user?.schema && META_ALLOWED_SCHEMAS.includes(user.schema);
    return (
        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h4 className={`flex items-center gap-2 text-xs font-bold ${titleColorClass} uppercase tracking-widest`}>
                    <div className={`p-1.5 rounded-lg ${iconContainerClass}`}>
                        {icon}
                    </div>
                    {title}
                </h4>
                <Badge color={badgeColor} size="xs" className="rounded-full">
                    {connectedSources.length}
                </Badge>
            </div>
            <div className="space-y-4 max-h-[calc(100dvh-399px)] overflow-y-auto">
                {platforms.map((platform) => {
                    const platformSources = connectedSources.filter(
                        (s) => s.type === platform.id && s.is_active
                    );
                    const isConnected = platformSources.length > 0;
                    const isComingSoon = platform.comingSoon && !(platform.id === 'meta_ads' && isMetaAllowed);

                    // Special logic for primary selection platforms
                    const isPrimarySelectionPlatform = platform.id === 'google_ads' || platform.id === 'google_admob' || platform.id === 'meta_ads';

                    const configuredSources = platformSources;

                    const totalAvailableAccounts = platformSources.reduce((acc, s) => acc + (s.accounts?.length || 0), 0);
                    const isWarning = isConnected && isPrimarySelectionPlatform && totalAvailableAccounts === 0;

                    const isProcessing = isAuthenticating && authenticatingPlatform === platform.id;

                    return (
                        <div
                            key={platform.id}
                            className={`p-4 rounded-xl border transition-all ${isComingSoon
                                ? 'border-gray-200 bg-gray-50 dark:border-neutral-700 dark:bg-neutral-800 opacity-70'
                                : isWarning
                                    ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10 shadow-sm shadow-amber-100/50'
                                    : isConnected
                                        ? platform.type === 'spending' ? 'border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20' : 'border-secondary-200 dark:border-secondary-800 bg-secondary-50 dark:bg-secondary-900/20'
                                        : 'border-gray-200 hover:border-gray-300 dark:border-neutral-700 dark:hover:border-neutral-600'
                                }`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-4">
                                    <img
                                        src={platform.icon}
                                        alt={platform.name}
                                        className={`w-12 h-12 ${isComingSoon ? 'grayscale' : ''}`}
                                    />
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-semibold text-gray-800 dark:text-gray-100 text-base">
                                                {platform.name}
                                            </p>
                                            {isComingSoon && (
                                                <Badge color="purple" size="xs">
                                                    {t('common.comingSoon')}
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-neutral-400">
                                            {isConnected ? (platform.type === 'spending' ? t('configurations.integrations.connectionEstablished') : t('configurations.integrations.sourceConnected')) : t(platform.description || '')}
                                        </p>
                                    </div>
                                </div>
                                {!isComingSoon && (
                                    <div className="flex items-center gap-2">
                                        {isWarning ? (
                                            <div className="flex items-center gap-2">
                                                <Tooltip content={t('configurations.integrations.switchAccount')}>
                                                    <Button
                                                        size="sm"
                                                        color="indigo"
                                                        className="bg-primary-600 hover:bg-primary-700 text-white border-none p-1"
                                                        onClick={() => onConnect(platform)}
                                                        isProcessing={isProcessing}
                                                    >
                                                        <ArrowsClockwiseIcon size={18} />
                                                    </Button>
                                                </Tooltip>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    size="xs"
                                                    className={isConnected ? "bg-white dark:bg-neutral-800 border-gray-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-700 p-1" : "bg-primary-600 hover:bg-primary-700 text-white border-none text-sm px-4"}
                                                    color={isConnected ? "light" : "indigo"}
                                                    onClick={() => onConnect(platform)}
                                                    isProcessing={isProcessing}
                                                    processingSpinner={<Spinner color="white" light size="sm" />}
                                                >
                                                    {isConnected ? (
                                                        <Tooltip content={t('configurations.integrations.addAccount')}>
                                                            <PlusIcon size={14} />
                                                        </Tooltip>
                                                    ) : (
                                                        <div className="flex items-center gap-1.5">
                                                            {!isProcessing && <PlusIcon size={16} />}
                                                            {t('configurations.integrations.connect')}
                                                        </div>
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Connected Accounts List */}
                            {isConnected && configuredSources.length > 0 && (
                                <div className={`mt-4 pt-4 border-t ${platform.type === 'spending' ? 'border-primary-100 dark:border-primary-800' : 'border-secondary-200 dark:border-secondary-800'} space-y-3`}>
                                    <p className="text-xs font-medium text-gray-500 dark:text-neutral-400">{t('configurations.integrations.connectedAccountsLabel')}</p>
                                    {configuredSources.map((source, idx) => (
                                        <div key={source.id || idx} className={`p-3 bg-white dark:bg-neutral-800 rounded-lg border ${platform.type === 'spending' ? 'border-primary-200 dark:border-primary-900' : 'border-secondary-100 dark:border-secondary-900'}`}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                                        {getSourceHeader(source, t)}
                                                    </p>
                                                    <ConnectedSourceDetails source={source} />
                                                </div>
                                                <div className="flex flex-col items-end gap-2 relative">
                                                    <div className="flex items-center gap-2">
                                                        {isPrimarySelectionPlatform && !isEditMode && (source.type === 'google_ads' || source.type === 'meta_ads') && (source.accounts?.length || 0) > 1 && (
                                                            <Tooltip content={t('configurations.integrations.clickToChangeAccount')}>
                                                                <Button
                                                                    size="xs"
                                                                    color="indigo"
                                                                    onClick={() => onSelectAccount(source, platform.id)}
                                                                >
                                                                    <ArrowSquareOutIcon size={14} />
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip content={t('configurations.integrations.disconnectAccount')}>
                                                            <Button
                                                                size="xs"
                                                                color="red"
                                                                onClick={() => onDisconnect(source, platform)}
                                                            >
                                                                <TrashIcon size={14} />
                                                            </Button>
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SourceConfigurationBlock;
